<p-confirmDialog />
<div class="version-form-container">
  <div class="header">
    <h1>{{ isEditMode ? 'Edit Version' : 'Add New Version' }}</h1>
    <div class="version-preview" *ngIf="versionForm">
      Version: <strong>{{ getVersionNumber() }}</strong>
    </div>
  </div>

  <div *ngIf="loading" class="loading">
    Loading version data...
  </div>

  <form [formGroup]="versionForm" (ngSubmit)="onSubmit()" *ngIf="!loading">
    <div class="form-grid">
      <!-- Version Numbers -->
      <div class="form-section">
        <h2>Version Numbers</h2>
        <div class="form-row">
          <div class="form-group">
            <label for="major">Major <span class="required">*</span></label>
            <input type="number" id="major" formControlName="major" min="0" class="form-control" />
            <div class="error" *ngIf="versionForm.get('major')?.invalid && versionForm.get('major')?.touched">
              Major version is required
            </div>
          </div>

          <div class="form-group">
            <label for="minor">Minor <span class="required">*</span></label>
            <input type="number" id="minor" formControlName="minor" min="0" class="form-control" />
            <div class="error" *ngIf="versionForm.get('minor')?.invalid && versionForm.get('minor')?.touched">
              Minor version is required
            </div>
          </div>

          <div class="form-group">
            <label for="patch">Patch <span class="required">*</span></label>
            <input type="number" id="patch" formControlName="patch" min="0" class="form-control" />
            <div class="error" *ngIf="versionForm.get('patch')?.invalid && versionForm.get('patch')?.touched">
              Patch version is required
            </div>
          </div>
        </div>
      </div>

      <!-- Basic Information -->
      <div class="form-section">
        <h2>Basic Information</h2>

        <div class="form-group">
          <label for="title">Title</label>
          <input type="text" id="title" formControlName="title" class="form-control" placeholder="Optional version title" />
        </div>

        <div class="form-group">
          <label>Role <span class="required">*</span></label>
          <div class="checkbox-group">
            <label class="checkbox-label" *ngFor="let role of availableRoles">
              <input type="checkbox" [checked]="isRoleSelected(role)" (change)="onRoleChange(role, $event)" />
              <span>{{ role }}</span>
            </label>
          </div>
          <div class="error" *ngIf="versionForm.get('role')?.invalid && versionForm.get('role')?.touched">
            At least one role is required
          </div>
        </div>

        <div class="form-group">
          <label for="language">Language <span class="required">*</span></label>
          <select id="language" formControlName="language" class="form-control">
            <option *ngFor="let lang of availableLanguages" [value]="lang">
              {{ lang }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="releaseDate">Release Date <span class="required">*</span></label>
          <input type="date" id="releaseDate" formControlName="releaseDate" class="form-control" />
          <div class="error" *ngIf="versionForm.get('releaseDate')?.invalid && versionForm.get('releaseDate')?.touched">
            Release date is required
          </div>
        </div>
      </div>

      <!-- Description -->
      <div class="form-section full-width">
        <h2>Description <span class="required">*</span></h2>

        <!-- XML Converter -->
        <div class="xml-converter">
          <label for="xmlInput">Convert XML to HTML (Optional)</label>
          <textarea id="xmlInput" [(ngModel)]="xmlInput" [ngModelOptions]="{standalone: true}" class="form-control xml-textarea" placeholder="Paste XML here and click convert..."></textarea>
          <button type="button" pButton label="Convert XML to HTML" class="p-button-secondary" (click)="convertXmlToHtml()">
          </button>
        </div>

        <!-- Markdown Converter -->
        <div class="markdown-converter">
          <label for="markdownInput">Convert Markdown to HTML (Optional)</label>
          <textarea id="markdownInput" [(ngModel)]="markdownInput" [ngModelOptions]="{standalone: true}" class="form-control markdown-textarea" placeholder="Paste Markdown here and click convert..."
            [disabled]="isConvertingMarkdown"></textarea>
          <button type="button" pButton class="p-button-secondary" (click)="convertMarkdownToHtml()" [disabled]="isConvertingMarkdown || !markdownInput.trim()" [loading]="isConvertingMarkdown" [label]="isConvertingMarkdown ? 'Converting...' : 'Convert Markdown to HTML'">
          </button>
        </div>

        <!-- Rich Text Editor and HTML View -->
        <div class="editor-container">
          <div class="editor-column">
            <label>Rich Text Editor</label>
            <quill-editor formControlName="description" [modules]="quillConfig" [sanitize]="true" (onContentChanged)="onDescriptionChange($event)" placeholder="Enter version description...">
            </quill-editor>
            <div class="error" *ngIf="versionForm.get('description')?.invalid && versionForm.get('description')?.touched">
              Description is required
            </div>
            <div class="hint">
              Note: All bullet lists will automatically have proper styling applied.
            </div>
          </div>

          <div class="editor-column">
            <label>Generated HTML</label>
            <textarea class="html-view" [value]="versionForm.get('description')?.value" (input)="onHtmlChange($event)" placeholder="HTML output will appear here..." spellcheck="false"></textarea>
            <div class="hint">
              You can edit the HTML directly here and it will update the rich text editor.
            </div>
          </div>
        </div>
      </div>

      <!-- Live Preview -->
      <div class="form-section full-width">
        <h2>Preview <span class="preview-hint">(as shown on customer portal)</span></h2>
        <div class="card">
          <h2 class="md:text-xl text-md font-bold mb-4">
            V&nbsp;{{ getVersionNumber() }}&nbsp;&nbsp;{{ versionForm.get('title')?.value }}
          </h2>
          <div class="border-l-2 border-red-500 pl-4 mb-6 version">
            <p class="md:text-base text-sm text-gray-500 font-semibold">{{ getReleaseDateFormatted() }}</p>
            <div class="p-3 md:text-base text-sm overflow-auto" [innerHTML]="versionForm.get('description')?.value"></div>
          </div>
        </div>
      </div>

      <!-- Read-only Audit Fields (for edit mode) -->
      <div class="form-section full-width" *ngIf="isEditMode && currentVersion">
        <h2>Audit Information (Read-only)</h2>
        <div class="audit-grid">
          <div class="audit-field">
            <label>Created By</label>
            <div class="readonly-value">{{ currentVersion.createdBy || 'N/A' }}</div>
          </div>
          <div class="audit-field">
            <label>Created At</label>
            <div class="readonly-value">{{ currentVersion.createdAt | date:'medium' }}</div>
          </div>
          <div class="audit-field">
            <label>Changed By</label>
            <div class="readonly-value">{{ currentVersion.changedBy || 'N/A' }}</div>
          </div>
          <div class="audit-field">
            <label>Changed At</label>
            <div class="readonly-value">{{ currentVersion.changedAt | date:'medium' }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Actions -->
    <div class="form-actions">
      <button type="button" pButton label="Cancel" class="p-button-secondary" (click)="onCancel()" [disabled]="saving">
      </button>
      <button type="submit" pButton class="p-button-primary" [disabled]="saving" [loading]="saving" [label]="isEditMode ? 'Update Version' : 'Add Version'">
      </button>
    </div>
  </form>
</div>